import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { buildProviders, Coerce, KalAutocompleteOption } from '@kalidea/kaligraphi';
import gql from 'graphql-tag';
import { catchError, filter, map, startWith, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { CustomFormElement } from 'src/app/_core/tools/custom-form-element';
import { FournisseurModelDto, MutationGetAchatFourInitModelArgs } from 'src/graphql/generated-types';
import { BehaviorSubject, merge, Observable, Subscription, throwError } from 'rxjs';

@Component({
    selector: 'app-shared-supplier-picker',
    templateUrl: './shared-supplier-picker.component.html',
    styleUrls: ['./shared-supplier-picker.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedSupplierPickerComponent),
    standalone: false
})
export class SharedSupplierPickerComponent extends CustomFormElement<any> implements OnInit {

    @Coerce('boolean')
    @Input()
    withReplenishementOption: boolean;

    @Coerce('boolean')
    @Input()
    required: boolean;

    @Input()
    filtersFunction: (item: KalAutocompleteOption<FournisseurModelDto>) => boolean = null;

    control = new UntypedFormControl('');

    suppliersWithPendingReplenishementArticlesControl = new UntypedFormControl(false);

    dataSource: KalAutocompleteOption<FournisseurModelDto>[] = [];

    subscription: Subscription;

    private _selectedSupplierId = new BehaviorSubject<number>(null);
    private _activityId = new BehaviorSubject<number>(null);

    constructor(private readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService) {
        super();
    }

    get activityId(): number {
        return this._activityId.getValue();
    }

    private get activityId$(): Observable<number> {
        return this._activityId.asObservable();
    }

    @Coerce('number')
    @Input()
    set activityId(value: number) {
        this._activityId.next(value);
    }

    private _selectedSupplier = new BehaviorSubject<FournisseurModelDto>(null);

    public get selectedSupplier(): FournisseurModelDto {
        return this._selectedSupplier.getValue();
    }

    get selectedSupplier$(): Observable<FournisseurModelDto> {
        return this._selectedSupplier.asObservable();
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    get displayedList(): KalAutocompleteOption<FournisseurModelDto>[] {
        const searchValue = (this.control.value || '').toLocaleLowerCase();
        if (!searchValue) {
            return this.dataSource.filter(e => this.filtersFunction?.(e) ?? true);
        }
        return this.dataSource
            .filter(e => this.filtersFunction?.(e) ?? true)
            .filter(({value: {NomFour, CodeFour}}) =>
                `${NomFour} ${CodeFour}`.toLocaleLowerCase().includes(searchValue)
            );
    }

    private get selectedSupplierId$(): Observable<number> {
        return this._selectedSupplierId.asObservable();
    }

    writeValue(value: number): void {
        this._selectedSupplierId.next(value);
        super.writeValue(value);
    }

    pickSupplier($event: KalAutocompleteOption<FournisseurModelDto>): void {
        if (!event) {
            return;
        }
        this._selectedSupplier.next($event.value);
        this.notifyUpdate(this.selectedSupplier?.IdFournisseur || null);
    }

    clearSupplier(): void {
        if (!this.selectedSupplier) {
            this.control.patchValue('', {emitEvent: false});
        }
    }

    private getSupplierList$(): Observable<FournisseurModelDto[]> {
        this.subscription?.unsubscribe();

        this.loading = true;
        return this.graphqlService.mutateOne<'GetAchatFourInitModel', MutationGetAchatFourInitModelArgs>({
            mutation: gql`
                mutation GetAchatFourInitModel($contexte: ContextInfosInput, $criteres: CritereAchatFourInitModelDTOInput) {
                    GetAchatFourInitModel(contexte: $contexte, criteres: $criteres) {
                        ListeAchatFourModel {
                            FournisseurModelDTO {
                                IdFournisseur
                                NomFour
                                IdCompteFraisComFour
                                MontFraisComFour
                                CodeFour
                                IsUtilisableSocial
                                IsUtilisableFonctionnement
                            }
                        }
                    }
                }
            `,
            variables: {
                criteres: {
                    WithFournisseur: true,
                    WithCommande: false,
                    WithStockCE: false,
                    WithCloture: false,
                    OnlyReapproAttente: this.suppliersWithPendingReplenishementArticlesControl.value,
                    IdActivite: this.activityId,
                }
            }
        }).pipe(
            map(GetAchatFourInitModel => GetAchatFourInitModel?.ListeAchatFourModel?.FournisseurModelDTO || []),
            tap(suppliers => {
                this.loading = false;
                this.dataSource = suppliers.map(supplier => ({label: supplier.NomFour, value: supplier}));

                this.subscription = this.selectedSupplierId$.pipe(
                    this.untilDestroyed,
                    tap(selectedSupplierId => {
                        this._selectedSupplier.next(suppliers.find(e => e.IdFournisseur === selectedSupplierId) || null);
                        this.control.patchValue(this.selectedSupplier?.NomFour || '', {emitEvent: false});
                    })
                ).subscribe();
            }),
            catchError(err => {
                this.loading = false;
                return throwError(err);
            })
        );
    }

    ngOnInit(): void {
        this.watch$ = merge(
            this.suppliersWithPendingReplenishementArticlesControl.valueChanges,
            this.activityId$
        ).pipe(
            switchMap(() => this.getSupplierList$())
        );

        this.watch$ = this.control.valueChanges.pipe(
            filter(() => !!this.selectedSupplier),
            tap(() => {
                this._selectedSupplier.next(null);
                this.notifyUpdate(null);
            })
        );
    }
}
